// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Support both frontend (Vite) and backend (Node.js) environments
const SUPABASE_URL = typeof window !== 'undefined' 
  ? import.meta.env?.VITE_SUPABASE_URL 
  : process.env.VITE_SUPABASE_URL || process.env.SUPABASE_URL;
  
const SUPABASE_ANON_KEY = typeof window !== 'undefined' 
  ? import.meta.env?.VITE_SUPABASE_ANON_KEY 
  : process.env.VITE_SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY;

export const supabaseConfig = {
  url: SUPABASE_URL,
  anonKey: SUPABASE_ANON_KEY,
};

export const hasSupabaseConfig = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

// Keep module imports safe long enough for App to render its explicit config
// boundary. Requests made with these values fail locally and cannot silently
// authenticate against a real Supabase project.
const SAFE_FALLBACK_URL = 'https://missing-supabase-config.invalid';
const SAFE_FALLBACK_ANON_KEY = 'missing-supabase-anon-key';

const isTestEnv = import.meta.env?.MODE === 'test';
// Reading window.localStorage throws a SecurityError in Safari with "Block All
// Cookies" on (and some private modes). This runs at import, so an unguarded
// read stopped the whole app from mounting. Without storage, supabase-js keeps
// the session in memory.
export function readSafeLocalStorage(): Storage | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    const storage = window.localStorage;
    return typeof storage?.getItem === 'function' &&
      typeof storage?.setItem === 'function' &&
      typeof storage?.removeItem === 'function'
      ? storage
      : undefined;
  } catch {
    return undefined;
  }
}
const safeLocalStorage = readSafeLocalStorage();

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(
  SUPABASE_URL || SAFE_FALLBACK_URL,
  SUPABASE_ANON_KEY || SAFE_FALLBACK_ANON_KEY,
  {
    auth: {
      persistSession: !isTestEnv,
      autoRefreshToken: !isTestEnv,
      // AuthCallback owns the PKCE exchange for `/auth/callback`. Letting the
      // client auto-detect the same `code` races the one-time exchange.
      detectSessionInUrl: false,
      storageKey: 'light-hunt-auth',
      // Enhanced cookie options for better cross-domain handling
      storage: safeLocalStorage,
      flowType: 'pkce',
      // Ensure session is maintained during external redirects
      debug: false,
      // Additional options for better session persistence. `retryDelay` is
      // not in this supabase-js version's option types; the spread-with-cast
      // keeps the runtime payload byte-identical while satisfying the
      // checker. Type-only change.
      ...({ retryDelay: 1000 } as Record<string, unknown>),
      // Use both localStorage and sessionStorage for redundancy during external redirects
      // Also not in this supabase-js version's option types; same
      // type-only spread-with-cast as retryDelay above.
      ...({ additionalHeaders: typeof window !== 'undefined' ? {
        'X-Client-Info': 'light-hunt-web'
      } : {} } as Record<string, unknown>),
    },
    global: {
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
        // Note: Removed 'Content-Type' to allow proper file uploads
        // Storage operations need to set their own content type
      }
    },
    db: {
      schema: 'public'
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);
